import { useDeepSignal } from '../context';
import { buildCompetitiveFromApi, competitiveInstrumentNote } from '../builders';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';
import { FunnelInsightBox } from '../components/funnel-insight-box';
import type { FunnelStageKey } from '../types';

const STAGE_LABELS: Record<FunnelStageKey, string> = {
  mentionRate: 'Mention Rate',
  top5Rate: 'Top-5 Rate',
  primaryRecRate: 'Primary Rec',
};

export function CompetitiveSection() {
  const { apiData, loading } = useDeepSignal();

  if (loading) return (
    <div style={{ padding: '32px 64px' }}>
      {[1,2,3,4,5].map(i => <DSLoadingRow key={i} />)}
    </div>
  );
  if (!apiData) return <DSEmptyState message="No competitive data available." />;

  const { funnelRows, perModelData, visibleStages, instruments } = buildCompetitiveFromApi(apiData);

  // No measured column at all: an honest empty state beats a table of dashes.
  if (visibleStages.length === 0) {
    return <DSEmptyState message="Competitive measurement is not available for this run yet." />;
  }

  const instrumentNote = competitiveInstrumentNote(instruments);
  // Funnel insights compare across all three stages; only meaningful when the
  // full funnel is measured.
  const fullFunnel = visibleStages.length === 3;

  const cellValue = (row: (typeof funnelRows)[number], key: FunnelStageKey) => {
    const v = row[key];
    return v == null ? '—' : `${v.toFixed(1)}%`;
  };

  const cellColor = (row: (typeof funnelRows)[number], key: FunnelStageKey) => {
    if (key === 'primaryRecRate' && row.focal && row.primaryRecRate != null) {
      return row.primaryRecRate >= 25 ? '#00c9c4' : row.primaryRecRate >= 15 ? '#28a9d2' : '#ff6d66';
    }
    return row.focal ? '#00c9c4' : 'var(--foreground)';
  };

  return (
    <div style={{ padding: '28px 0 40px' }}>

      {/* ── Full Recommendation Funnel ───────────────────────────────────── */}
      <div style={{ padding: '0 32px 28px' }}>
        <SectionLabel>Competitor Recommendation Funnel</SectionLabel>
        <SectionTitle size="xl">Where each competitor stands at every stage of the funnel.</SectionTitle>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.5', marginBottom: instrumentNote ? '8px' : '20px' }}>
          Mention rate only tells part of the story. This shows how each competitor performs at each stage of the funnel, from being mentioned to making the shortlist to becoming the primary recommendation. Your own funnel is on the main funnel view.
        </p>
        {instrumentNote && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', lineHeight: '1.5', marginBottom: '20px' }}>
            {instrumentNote}
          </p>
        )}

        <div style={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
          {/* Funnel table */}
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            {/* Header */}
            <div style={{ display: 'flex', padding: '0 16px 10px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ flex: 2, fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.2px', color: 'var(--muted-foreground)' }}>Brand</span>
              {visibleStages.map(key => (
                <span key={key} style={{ flex: 1, textAlign: 'right', fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.2px', color: 'var(--muted-foreground)' }}>
                  {STAGE_LABELS[key]}
                </span>
              ))}
            </div>
            {funnelRows.map((row, i) => (
              <div key={row.brand} style={{
                display: 'flex', alignItems: 'center', padding: '14px 16px',
                borderLeft: row.focal ? '3px solid #00c9c4' : '3px solid transparent',
                borderBottom: i < funnelRows.length - 1 ? '1px solid var(--border)' : 'none',
                backgroundColor: row.focal ? '#f0fefe' : 'transparent',
              }}>
                <span style={{ flex: 2, fontSize: 'var(--text-sm)', fontWeight: row.focal ? 600 : 400, color: row.focal ? '#00c9c4' : 'var(--foreground)' }}>
                  {row.brand}
                </span>
                {visibleStages.map(key => (
                  <span key={key} style={{
                    flex: 1, textAlign: 'right', fontSize: 'var(--text-sm)',
                    fontWeight: row.focal ? 600 : 400,
                    color: cellColor(row, key),
                  }}>
                    {cellValue(row, key)}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {/* Funnel Insights sidebar */}
          {fullFunnel && funnelRows.length > 1 && <FunnelInsightBox data={funnelRows} />}
        </div>
      </div>

      {/* ── Per-Model Competitive Presence ──────────────────────────────── */}
      <div style={{ padding: '28px 32px 0' }}>
        <div style={{ height: '1px', backgroundColor: 'var(--border)', marginBottom: '24px' }} />
        <SectionLabel>Per-Model Competitive Presence</SectionLabel>
        <SectionTitle size="xl">How each AI model ranks the competitive field.</SectionTitle>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.5', marginBottom: '20px' }}>
          AI models don't all reason the same way. This breaks mention rate down by model, so you can see where the competitive picture shifts depending on which AI you ask.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {perModelData.map(card => {
            const maxVal = Math.max(...card.brands.map(b => b.value ?? 0), 1);
            return (
              <div key={card.model} style={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', backgroundColor: 'var(--background)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: card.dotColor, flexShrink: 0 }} />
                  <span style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)' }}>{card.model}</span>
                </div>
                <div style={{ padding: '6px 0' }}>
                  {card.brands.map(b => (
                    <div key={b.brand} style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '9px 20px',
                    }}>
                      <span style={{
                        width: '88px', flexShrink: 0, fontSize: 'var(--text-sm)',
                        fontWeight: b.focal ? 600 : 400,
                        color: b.focal ? '#00c9c4' : 'var(--foreground)',
                      }}>{b.brand}</span>
                      <div style={{ flex: 1, height: '4px', backgroundColor: '#eef0f3', borderRadius: '2px' }}>
                        <div style={{
                          width: `${b.value == null ? 0 : (b.value / maxVal) * 100}%`, height: '100%', borderRadius: '2px',
                          backgroundColor: b.focal ? '#00c9c4' : card.dotColor,
                          opacity: b.focal ? 1 : 0.45,
                        }} />
                      </div>
                      <span style={{
                        width: '44px', textAlign: 'right', flexShrink: 0, fontSize: 'var(--text-sm)',
                        fontWeight: b.focal ? 600 : 400,
                        color: b.focal ? '#00c9c4' : 'var(--foreground)',
                      }}>
                        {b.value == null ? '—' : `${b.value.toFixed(1)}%`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
